import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as MonitoringService from "../../services/Monitoring";

const initialState = {
  enabled: false,
  expiresAt: null,
  remainingMs: 0,
  durationMin: null,
  traces: [],
  selectedTrace: null,
  statusLoading: false,
  tracesLoading: false,
  traceLoading: false,
  toggleLoading: false,
  error: null,
};

export const fetchMonitoringStatus = createAsyncThunk(
  "monitoring/fetchStatus",
  async (siteId, { rejectWithValue }) => {
    try {
      return await MonitoringService.getMonitoringStatus(siteId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const toggleMonitoring = createAsyncThunk(
  "monitoring/toggle",
  async ({ siteId, enable, durationMin }, { rejectWithValue }) => {
    try {
      if (enable) {
        return await MonitoringService.enableMonitoring(siteId, durationMin);
      } else {
        return await MonitoringService.disableMonitoring(siteId);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const fetchTraces = createAsyncThunk(
  "monitoring/fetchTraces",
  async ({ siteId, search }, { rejectWithValue }) => {
    try {
      return await MonitoringService.listTraces(siteId, { search });
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const fetchTraceDetail = createAsyncThunk(
  "monitoring/fetchTraceDetail",
  async ({ siteId, traceId }, { rejectWithValue }) => {
    try {
      return await MonitoringService.getTrace(siteId, traceId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const clearAllTraces = createAsyncThunk(
  "monitoring/clearTraces",
  async (siteId, { rejectWithValue }) => {
    try {
      return await MonitoringService.clearTraces(siteId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    clearSelectedTrace: (state) => {
      state.selectedTrace = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Status
      .addCase(fetchMonitoringStatus.pending, (state) => {
        state.statusLoading = true;
      })
      .addCase(fetchMonitoringStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.enabled = action.payload.enabled;
        state.expiresAt = action.payload.expiresAt || null;
        state.remainingMs = action.payload.remainingMs || 0;
        state.durationMin = action.payload.durationMin || null;
      })
      .addCase(fetchMonitoringStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.error = action.payload;
      })
      // Toggle
      .addCase(toggleMonitoring.pending, (state) => {
        state.toggleLoading = true;
      })
      .addCase(toggleMonitoring.fulfilled, (state, action) => {
        state.toggleLoading = false;
        state.enabled = action.payload.enabled;
        state.expiresAt = action.payload.expiresAt || null;
        state.durationMin = action.payload.durationMin || null;
        state.remainingMs =
          action.payload.enabled && action.payload.expiresAt
            ? Math.max(
                0,
                new Date(action.payload.expiresAt).getTime() - Date.now(),
              )
            : 0;
      })
      .addCase(toggleMonitoring.rejected, (state, action) => {
        state.toggleLoading = false;
        state.error = action.payload;
      })
      // Traces list
      .addCase(fetchTraces.pending, (state) => {
        state.tracesLoading = true;
      })
      .addCase(fetchTraces.fulfilled, (state, action) => {
        state.tracesLoading = false;
        state.traces = action.payload.traces || [];
        if (action.payload.enabled !== undefined) {
          state.enabled = action.payload.enabled;
        }
      })
      .addCase(fetchTraces.rejected, (state, action) => {
        state.tracesLoading = false;
        state.error = action.payload;
      })
      // Trace detail
      .addCase(fetchTraceDetail.pending, (state) => {
        state.traceLoading = true;
      })
      .addCase(fetchTraceDetail.fulfilled, (state, action) => {
        state.traceLoading = false;
        state.selectedTrace = action.payload.trace;
      })
      .addCase(fetchTraceDetail.rejected, (state, action) => {
        state.traceLoading = false;
        state.error = action.payload;
      })
      // Clear traces
      .addCase(clearAllTraces.fulfilled, (state) => {
        state.traces = [];
        state.selectedTrace = null;
      });
  },
});

export const { clearSelectedTrace, clearError } = monitoringSlice.actions;
export default monitoringSlice.reducer;
